import { UIDiv, UIInput } from '@ui';

export default class Autocomplete {
  constructor(options = {}) {
    this.container = null;
    this.input = null;
    this.suggestionInput = null;
    this.dropdownContainer = null;
    this.onSelect = options.onSelect || (() => {});
    this.onEnterWithoutSelection =
      options.onEnterWithoutSelection || (() => {});
    this.items = options.items || [];
    this.excludeItems = options.excludeItems || [];
    this.selectedIndex = -1;
    this.filteredItems = [];

    // customization
    this.placeholder = options.placeholder || '';
    this.allowMultiple = options.allowMultiple || false;
    this.delimiter = options.delimiter || /[\s,]+/;
    this.filterFn = options.filterFn || this.defaultFilterFn;
    this.getItemText = options.getItemText || (item => item);
    this.renderDropdownItem =
      options.renderDropdownItem || this.defaultRenderDropdownItem;
  }

  defaultFilterFn(item, searchText) {
    const itemText = this.getItemText(item).toLowerCase();
    return itemText.includes(searchText.toLowerCase());
  }

  defaultRenderDropdownItem(item) {
    const div = new UIDiv();
    div.setClass('px-4 py-3 hover:bg-gray-50 cursor-pointer');
    div.dom.textContent = this.getItemText(item);
    return div;
  }

  render() {
    const container = new UIDiv();
    container.setClass('relative flex-1 min-w-[120px]');
    this.container = container;

    const inputWrapper = new UIDiv();
    inputWrapper.setClass('relative w-full');

    this.suggestionInput = new UIInput('');
    this.suggestionInput.setClass(
      'absolute w-full h-[40px] !outline-none !border-none bg-transparent text-sm text-gray-300 pointer-events-none !shadow-none',
    );
    this.suggestionInput.setType('text');
    this.suggestionInput.setDisabled(true);

    this.input = new UIInput('', { defaultStyle: false });
    this.input.setClass(
      'w-full h-[40px] !outline-none !border-none bg-transparent text-sm relative !shadow-none',
    );
    this.input.setType('text');
    this.input.setPlaceholder(this.placeholder);

    this.dropdownContainer = new UIDiv();
    this.dropdownContainer.setClass(
      'absolute left-0 right-0 top-full mt-1 bg-white rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto hidden border border-gray-200',
    );

    inputWrapper.add(this.suggestionInput, this.input);
    container.add(inputWrapper, this.dropdownContainer);

    this.setupEventListeners();

    return container;
  }

  setupEventListeners() {
    this.input.onInput(() => this.handleInput());
    this.input.onKeyDown(e => this.handleKeyDown(e));

    document.addEventListener('click', e => {
      if (!this.container.contains(e.target)) {
        this.hideDropdown();
      }
    });
  }

  handleInput() {
    const value = this.input.getValue().trim();

    if (!value) {
      this.hideDropdown();
      this.suggestionInput.setValue('');
      return;
    }

    if (this.allowMultiple && (value.includes(',') || value.includes(' '))) {
      const items = value.split(this.delimiter).filter(item => item.length > 0);
      if (items.length > 1) {
        let allSuccessful = true;
        items.forEach(item => {
          const success = this.onEnterWithoutSelection(item.trim());
          if (!success) allSuccessful = false;
        });
        if (allSuccessful) {
          this.clearInput();
        }
        return;
      }
    }

    this.updateSuggestions(value);
  }

  handleKeyDown(e) {
    switch (e.key) {
      case 'Tab':
        e.preventDefault();
        if (this.selectedIndex >= 0) {
          this.selectItem(this.filteredItems[this.selectedIndex]);
        } else if (this.filteredItems.length > 0) {
          this.selectItem(this.filteredItems[0]);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        this.navigateDropdown(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.navigateDropdown(-1);
        break;
      case 'Enter':
        e.preventDefault();
        if (this.selectedIndex >= 0) {
          this.selectItem(this.filteredItems[this.selectedIndex]);
        } else {
          const value = this.input.getValue().trim();
          if (value) {
            if (this.allowMultiple) {
              const items = value
                .split(this.delimiter)
                .filter(item => item.length > 0);
              let allSuccessful = true;
              items.forEach(item => {
                const success = this.onEnterWithoutSelection(item.trim());
                if (!success) allSuccessful = false;
              });
              if (allSuccessful) {
                this.clearInput();
              }
            } else {
              if (this.onEnterWithoutSelection(value)) {
                this.clearInput();
              }
            }
          }
        }
        break;
      case 'Escape':
        this.hideDropdown();
        break;
    }
  }

  updateSuggestions(inputText) {
    this.filteredItems = this.items.filter(
      item =>
        this.filterFn(item, inputText) && !this.excludeItems.includes(item),
    );

    if (this.filteredItems.length > 0) {
      this.showDropdown();
      this.selectedIndex = 0;
      this.updateSelectedItem();

      const firstMatch = this.filteredItems[0];
      const firstMatchText = this.getItemText(firstMatch);

      if (firstMatchText.toLowerCase().startsWith(inputText.toLowerCase())) {
        this.suggestionInput.setValue(
          inputText + firstMatchText.slice(inputText.length),
        );
      } else {
        this.suggestionInput.setValue('');
      }
    } else {
      this.hideDropdown();
      this.suggestionInput.setValue('');
    }

    this.renderDropdown();
  }

  renderDropdown() {
    this.dropdownContainer.clear();

    this.filteredItems.forEach((item, index) => {
      const itemElement = this.renderDropdownItem(item);

      itemElement.onClick(() => this.selectItem(item));
      itemElement.onMouseOver(() => {
        this.selectedIndex = index;
        this.updateSelectedItem();
      });

      this.dropdownContainer.add(itemElement);
    });
  }

  selectItem(item) {
    const itemText = this.getItemText(item);
    this.input.setValue(itemText);
    this.suggestionInput.setValue('');
    this.hideDropdown();
    this.onSelect(item);
  }

  navigateDropdown(direction) {
    if (this.filteredItems.length === 0) return;

    this.selectedIndex =
      (this.selectedIndex + direction + this.filteredItems.length) %
      this.filteredItems.length;
    this.updateSelectedItem();

    const selectedItem = this.filteredItems[this.selectedIndex];
    const inputText = this.input.getValue().trim();
    const suggestionText = this.getItemText(selectedItem);

    if (suggestionText.toLowerCase().startsWith(inputText.toLowerCase())) {
      this.suggestionInput.setValue(
        inputText + suggestionText.slice(inputText.length),
      );
    } else {
      this.suggestionInput.setValue('');
    }
  }

  updateSelectedItem() {
    const items = this.dropdownContainer.getChildren();
    items.forEach((item, index) => {
      if (index === this.selectedIndex) {
        item.classList.add('bg-gray-50');
      } else {
        item.classList.remove('bg-gray-50');
      }
    });
  }

  showDropdown() {
    this.dropdownContainer.removeClass('hidden');
  }

  hideDropdown() {
    this.dropdownContainer.addClass('hidden');
    this.selectedIndex = -1;
  }

  getCurrentValue() {
    return this.input.getValue().trim();
  }

  clearInput() {
    this.input.setValue('');
    this.suggestionInput.setValue('');
    this.hideDropdown();
  }
}
